import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { XCircle } from "lucide-react";

/**
 * QR-scan landing page : /q/{queueId}
 *
 * Spec utilisateur 2026-02 (fork courant) : un visiteur qui scanne un QR
 * code ne doit JAMAIS se voir demander de se connecter. Le flux doit être
 * strictement identique à celui de l'app web : on tombe sur la fiche
 * établissement (/commerce/{commerceId}) avec la file pré-sélectionnée,
 * et le bouton "Prendre un ticket" passe en Guest Mode.
 *
 * Cette page joue uniquement le rôle de pivot : on appelle l'endpoint
 * public pour récupérer le commerceId de la file scannée puis on bascule
 * (replace: true) sur /commerce/{commerceId}?queueId=X. Si la file
 * n'existe pas / est désactivée, on affiche un écran d'erreur clair.
 */
export default function PublicQueueLanding() {
  const { queueId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/queues/${queueId}/public`);
        if (!mounted) return;
        if (!data?.commerceId) {
          setError("Cette file n'est plus disponible");
          return;
        }
        // Redirige immédiatement vers la fiche établissement avec la file
        // pré-sélectionnée → exactement le même flux que via l'app.
        // `replace: true` évite que le visiteur revienne sur cette page
        // intermédiaire avec le bouton "Retour" du navigateur. Le paramètre
        // `from=qr` permet à la fiche d'afficher un toast de bienvenue
        // éphémère, distinguant ainsi le parcours scan du parcours app
        // classique (fork 19+).
        navigate(`/commerce/${data.commerceId}?queueId=${queueId}&from=qr`, { replace: true });
      } catch (e) {
        if (mounted) {
          setError(e?.response?.data?.detail || "Cette file n'est plus disponible");
        }
      }
    })();
    return () => { mounted = false; };
  }, [queueId, navigate]);

  if (error) {
    return (
      <div className="container mx-auto px-4 md:px-8 py-12 max-w-xl">
        <div className="border-2 border-destructive/40 bg-destructive/5 p-6 text-center" data-testid="queue-not-available">
          <XCircle size={42} className="text-destructive mx-auto" />
          <h1 className="font-display text-2xl font-black text-ink mt-3">{error}</h1>
          <p className="text-sm text-subtle mt-2">Vérifiez l'adresse ou contactez l'établissement.</p>
          <Link to="/" className="inline-block mt-5 text-sm text-ink underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  // Brief loading state during the redirect.
  return (
    <div className="container mx-auto px-4 py-16 text-center text-subtle" data-testid="public-queue-landing-redirect">
      Ouverture de votre file d'attente…
    </div>
  );
}
